import { DeleteOutlined, EditOutlined, LinkOutlined, UserOutlined } from '@ant-design/icons';
import {
  IDEA_FREQUENCY_LABELS,
  IDEA_IMPACT_LABELS,
  IDEA_REACH_LABELS,
  IDEA_STATUSES,
  canModerateIdeas,
} from '@cult/domain';
import type { PublicIdea } from '@cult/schemas';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  App as AntApp,
  Avatar,
  Button,
  Card,
  Divider,
  Flex,
  Popconfirm,
  Result,
  Skeleton,
  Space,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { BackButton, CategoryTag, EditWindowNotice, PageContainer } from '../../components';
import { AttachmentsView } from '../../features/attachments';
import {
  EditIdeaModal,
  IdeaContent,
  IdeaStatusHistory,
  IdeaStatusTag,
  StatusTransitionPanel,
  useCopyIdeaLink,
  useDeleteIdea,
  useIdea,
} from '../../features/ideas';

import { useAuthMe } from '../../features/auth';
import styles from './ideas.$id.module.scss';

const { Title, Paragraph, Text } = Typography;

const AuthorAvatar = ({ author }: { author: PublicIdea['author'] }) => {
  if (!author) return <Avatar size={32} icon={<UserOutlined />} />;
  if (author.avatarUrl) return <Avatar size={32} src={author.avatarUrl} />;
  return <Avatar size={32}>{author.displayName.charAt(0).toUpperCase()}</Avatar>;
};

const CopyLinkButton = ({ ideaNumber }: { ideaNumber: number }) => {
  const copy = useCopyIdeaLink(ideaNumber);
  const { token } = theme.useToken();
  return (
    <Button
      type="text"
      size="small"
      icon={<LinkOutlined />}
      onClick={copy}
      style={{ color: token.colorTextTertiary, fontSize: 12 }}
    >
      Copy link
    </Button>
  );
};

const IdeaDetailSkeleton = () => {
  return (
    <div>
      <Skeleton.Button active style={{ width: 110, marginBottom: 24 }} />
      <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1 }} style={{ marginBottom: 24 }} />
      <div
        className={styles.grid}
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 260px',
          gap: 32,
          alignItems: 'start',
        }}
      >
        <Skeleton active title={false} paragraph={{ rows: 7 }} />
        <Flex vertical gap={16} style={{ paddingLeft: 20 }}>
          <Skeleton.Button active block style={{ height: 48 }} />
          <Skeleton avatar active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        </Flex>
      </div>
    </div>
  );
};

const IdeaDetailPage = () => {
  const { id } = Route.useParams();
  const ideaQuery = useIdea(id);
  const auth = useAuthMe();
  const { token } = theme.useToken();
  const [editOpen, setEditOpen] = useState(false);
  const deleteIdea = useDeleteIdea();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();

  if (ideaQuery.isPending) {
    return (
      <PageContainer>
        <IdeaDetailSkeleton />
      </PageContainer>
    );
  }

  if (ideaQuery.data === null) {
    return (
      <PageContainer>
        <Result
          status="404"
          title="Idea not found"
          subTitle="It may have been removed or you used a stale link."
          extra={
            <Link to="/ideas">
              <Button type="primary">Back to feed</Button>
            </Link>
          }
        />
      </PageContainer>
    );
  }

  if (ideaQuery.isError) {
    return (
      <PageContainer>
        <Result
          status="error"
          title="Couldn't load idea"
          subTitle={ideaQuery.error.message}
          extra={
            <Link to="/ideas">
              <Button>Back to feed</Button>
            </Link>
          }
        />
      </PageContainer>
    );
  }

  const idea = ideaQuery.data;
  const authorName = idea.author ? idea.author.displayName : 'Anonymous';
  const accentColor = idea.category.color ?? token.colorBorderSecondary;
  const isAuthor = idea.isMyIdea;
  // The author may edit or delete while the idea is KIV and inside the edit window (same rule in the API);
  // the notice hides itself, and with it the buttons, once the window closes.
  const canEdit = isAuthor && idea.status === IDEA_STATUSES.KIV;

  const onDelete = () =>
    deleteIdea.mutate(idea, {
      onSuccess: () => {
        message.success('Idea deleted');
        navigate({ to: '/ideas' });
      },
      onError: (err) => message.error(`Couldn't delete idea: ${err.message}`),
    });

  return (
    <PageContainer>
      <div>
        {/* Back nav */}
        <BackButton fallbackTo="/ideas" />

        {canEdit ? (
          <div className={styles.notice}>
            <EditWindowNotice
              createdAt={idea.createdAt}
              noun="idea"
              actions={
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this idea?"
                    description="This can't be undone."
                    okText="Delete"
                    okButtonProps={{ danger: true, loading: deleteIdea.isPending }}
                    onConfirm={onDelete}
                  >
                    <Button danger size="small" icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              }
            />
          </div>
        ) : null}

        {/* 2-column grid */}
        <div
          className={styles.grid}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 280px',
            gap: 20,
            alignItems: 'start',
          }}
        >
          {/* Left column — main card + status history + comments stacked */}
          <Flex vertical gap={20}>
            {/* Main card — 3px category-color left accent ties it back to feed rows */}
            <Card
              className={styles.main}
              styles={{ body: { padding: 24 } }}
              style={{
                borderLeft: `3px solid ${accentColor}`,
                animation: 'scale-in var(--dur-normal) var(--ease-out) both',
              }}
            >
              {/* Title — first thing the eye lands on */}
              <Title level={2} style={{ margin: '0 0 12px' }}>
                {idea.title}
              </Title>

              {/* Body — rendered markdown */}
              <IdeaContent idea={idea} />

              {/* Attachments — image grid + doc cards beneath the body */}
              <AttachmentsView attachments={idea.attachments} />

              {/* Decision note — bordered inner block, distinct from the body */}
              {idea.decisionNote ? (
                <div
                  style={{
                    marginTop: 20,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    padding: '12px 16px',
                    background: token.colorFillQuaternary,
                  }}
                >
                  <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
                    Decision note
                  </Text>
                  <Paragraph type="secondary" style={{ whiteSpace: 'pre-wrap', marginBottom: 0, fontSize: 13 }}>
                    {idea.decisionNote}
                  </Paragraph>
                </div>
              ) : null}
            </Card>

            {/* Status history — moderators only */}
            {auth.data && canModerateIdeas(auth.data.role) ? (
              <Card
                styles={{ body: { padding: 24 } }}
                style={{ animation: 'fade-in var(--dur-normal) var(--ease-out) 80ms both' }}
              >
                <Title level={4} style={{ margin: '0 0 16px', letterSpacing: '-0.015em' }}>
                  Status history
                </Title>
                <IdeaStatusHistory ideaId={idea.id} enabled />
              </Card>
            ) : null}
          </Flex>

          {/* Sidebar column — sticky card + tiny copy-link action below it */}
          <Flex
            vertical
            gap={8}
            style={{
              position: 'sticky',
              top: 20,
              animation: 'slide-up var(--dur-normal) var(--ease-out) 100ms both',
            }}
          >
            <Card className={styles.aside} styles={{ body: { padding: 20 } }}>
              {idea.author ? (
                <>
                  <Flex align="center" gap={10} style={{ marginBottom: 14 }}>
                    <AuthorAvatar author={idea.author} />
                    <Flex vertical gap={1} style={{ minWidth: 0 }}>
                      <Text strong style={{ fontSize: 13, lineHeight: 1.4 }}>
                        {authorName}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                        {dayjs(idea.createdAt).format('MMM D, YYYY')}
                      </Text>
                    </Flex>
                  </Flex>
                  <Divider style={{ margin: '0 0 14px' }} />
                </>
              ) : null}

              {/* Metadata rows */}
              <Flex vertical gap={10}>
                <Flex align="center" justify="space-between">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Status
                  </Text>
                  <IdeaStatusTag status={idea.status} />
                </Flex>
                <Flex align="center" justify="space-between">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Category
                  </Text>
                  <CategoryTag category={idea.category} />
                </Flex>
                {idea.frequency !== null ? (
                  <Flex align="center" justify="space-between" gap={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Frequency
                    </Text>
                    <Text style={{ fontSize: 12, textAlign: 'right' }}>{IDEA_FREQUENCY_LABELS[idea.frequency]}</Text>
                  </Flex>
                ) : null}
                {idea.reach !== null ? (
                  <Flex align="center" justify="space-between" gap={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Reach
                    </Text>
                    <Text style={{ fontSize: 12, textAlign: 'right' }}>{IDEA_REACH_LABELS[idea.reach]}</Text>
                  </Flex>
                ) : null}
                {idea.impact !== null ? (
                  <Flex align="center" justify="space-between" gap={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Impact
                    </Text>
                    <Text style={{ fontSize: 12, textAlign: 'right' }}>{IDEA_IMPACT_LABELS[idea.impact]}</Text>
                  </Flex>
                ) : null}
                <Flex align="center" justify="space-between">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Number
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    #{idea.number}
                  </Text>
                </Flex>
                <Flex align="center" justify="space-between">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Submitted
                  </Text>
                  <Tooltip title={dayjs(idea.createdAt).format('MMM D, YYYY h:mm A')}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(idea.createdAt).fromNow()}
                    </Text>
                  </Tooltip>
                </Flex>
                {idea.closedAt ? (
                  <Flex align="center" justify="space-between">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Closed
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(idea.closedAt).format('MMM D, YYYY')}
                    </Text>
                  </Flex>
                ) : null}
              </Flex>

              <StatusTransitionPanel idea={idea} />
            </Card>
            <Flex justify="center">
              <CopyLinkButton ideaNumber={idea.number} />
            </Flex>
          </Flex>
        </div>

        {isAuthor ? <EditIdeaModal idea={idea} open={editOpen} onClose={() => setEditOpen(false)} /> : null}
      </div>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_auth/ideas/$id')({
  component: IdeaDetailPage,
});
